import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Search from '@mui/icons-material/Search';
import Close from '@mui/icons-material/Close';

/** One height for every filter control so the bars read as a single unit. */
export const FILTER_CONTROL_HEIGHT = 32;
const FILTER_FONT_SIZE = 14;
// Pill-shaped controls on a white fill, matching the chip silhouette,
// so search, selects, and chips read as one family.
const FILTER_RADIUS = FILTER_CONTROL_HEIGHT / 2;
const FILTER_BG = 'white';

/**
 * Shared filter helpers and controls for the jury box, dismissed area,
 * and jury pool. All matching is done client-side over already-decrypted
 * juror objects, so filters apply instantly with no Dexie round-trips.
 */

/** Case-insensitive text match over name, comments, and demographic values. */
export function jurorMatchesText(juror, term) {
  if (!term) return true;
  const t = term.toLowerCase();
  if (juror.nameAndId?.toLowerCase().includes(t)) return true;
  if (juror.comments?.toLowerCase().includes(t)) return true;
  return (juror.demographics || []).some(
    (d) => d?.value?.toLowerCase?.().includes(t),
  );
}

/**
 * Faceted demographic match: OR within a category, AND across categories.
 * @param {Object} juror
 * @param {Array<{name: string, value: string}>} selections
 */
export function jurorMatchesDemographics(juror, selections) {
  if (!selections || selections.length === 0) return true;

  const byCategory = new Map();
  for (const sel of selections) {
    if (!byCategory.has(sel.name)) byCategory.set(sel.name, []);
    byCategory.get(sel.name).push(sel.value);
  }

  for (const [name, values] of byCategory) {
    const jurorValues = (juror.demographics || [])
      .filter((d) => d?.name === name)
      .map((d) => d.value);
    if (!values.some((v) => jurorValues.includes(v))) {
      return false;
    }
  }
  return true;
}

/**
 * OR across the selected reason chips. 'both' is its own reason, distinct
 * from the single-side strikes (matching how it is scored and displayed).
 */
export function jurorMatchesReasons(juror, reasonKeys) {
  if (!reasonKeys || reasonKeys.length === 0) return true;
  return reasonKeys.some((key) => {
    if (key === 'cause' || key === 'hardship') {
      return juror.discardedReason === key;
    }
    if (key === 'prosecution') {
      return juror.discardedReason === 'peremptory' && juror.discarder === 'prosecutor';
    }
    if (key === 'defense') {
      return juror.discardedReason === 'peremptory' && juror.discarder === 'defense';
    }
    if (key === 'both') {
      return juror.discardedReason === 'peremptory' && juror.discarder === 'both';
    }
    return false;
  });
}

export const JUROR_STATUSES = [
  { key: 'keep', label: 'Keep', tooltip: 'Intend to keep' },
  { key: 'question', label: 'Question', tooltip: 'Question further' },
  { key: 'dismiss', label: 'Dismiss', tooltip: 'Intend to dismiss' },
];

/** OR across the selected intent statuses. */
export function jurorMatchesStatuses(juror, statusKeys) {
  if (!statusKeys || statusKeys.length === 0) return true;
  return statusKeys.includes(juror.status);
}

/**
 * Shared filter state for the common filters (text search + demographics)
 * across the jury box, dismissed area, and jury pool. Searching in one area
 * searches them all; area-specific filters (intent, dismissal reason) stay
 * local to their components. Living above the areas also means the dismissed
 * filters survive the DismissedBox -> DismissQuadrant swap during drags.
 */
const SharedJurorFiltersContext = React.createContext(null);

export function SharedJurorFiltersProvider({ children }) {
  const [search, setSearch] = React.useState('');
  const [demographics, setDemographics] = React.useState([]);

  const value = React.useMemo(
    () => ({ search, setSearch, demographics, setDemographics }),
    [search, demographics],
  );

  return (
    <SharedJurorFiltersContext.Provider value={value}>
      {children}
    </SharedJurorFiltersContext.Provider>
  );
}

/** Falls back to component-local state when no provider is mounted. */
export function useSharedJurorFilters() {
  const ctx = React.useContext(SharedJurorFiltersContext);
  const [localSearch, setLocalSearch] = React.useState('');
  const [localDemographics, setLocalDemographics] = React.useState([]);

  return (
    ctx ?? {
      search: localSearch,
      setSearch: setLocalSearch,
      demographics: localDemographics,
      setDemographics: setLocalDemographics,
    }
  );
}

/** Debounce fast-changing values (e.g. search keystrokes) before filtering. */
export function useDebouncedValue(value, delayMs = 150) {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Single-line container for an area's filter controls. Wraps only when
 * the viewport genuinely can't fit them.
 */
export function FilterBar({ children, style }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/**
 * The standard text search field used by every filter bar — same height,
 * font, icon, and clear affordance everywhere.
 */
export function FilterSearchField({ value, onChange, placeholder = 'Search', width = 200 }) {
  return (
    <TextField
      size="small"
      variant="outlined"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width }}
      InputProps={{
        style: {
          height: FILTER_CONTROL_HEIGHT,
          fontSize: FILTER_FONT_SIZE,
          backgroundColor: FILTER_BG,
          borderRadius: FILTER_RADIUS,
        },
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" style={{ color: 'rgba(0,0,0,0.35)' }} />
          </InputAdornment>
        ),
        endAdornment: value.length > 0 ? (
          <InputAdornment position="end">
            <IconButton size="small" style={{ padding: 2 }} onClick={() => onChange('')}>
              <Close fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
}

/**
 * Generic row of toggleable filter chips.
 * @param {{
 *   options: Array<{key: string, label: string, tooltip?: string}>,
 *   value: string[],
 *   onChange: (keys: string[]) => void,
 * }} props
 */
export function FilterChips({ options, value, onChange }) {
  const toggle = (key) => {
    onChange(
      value.includes(key)
        ? value.filter((k) => k !== key)
        : [...value, key],
    );
  };

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map(({ key, label, tooltip }) => {
        const active = value.includes(key);
        const chip = (
          <Chip
            key={key}
            label={label}
            clickable
            color={active ? 'primary' : 'default'}
            variant={active ? 'filled' : 'outlined'}
            onClick={() => toggle(key)}
            style={{
              height: FILTER_CONTROL_HEIGHT,
              fontSize: FILTER_FONT_SIZE,
              backgroundColor: active ? undefined : FILTER_BG,
            }}
          />
        );
        return tooltip ? (
          <Tooltip key={key} title={tooltip}>
            {chip}
          </Tooltip>
        ) : (
          chip
        );
      })}
    </div>
  );
}

/**
 * Dismissal reason chips. Strike labels follow the case type
 * (Plaintiff for civil, Prosecution for criminal), and the separate
 * "Both" reason only appears when it is actually in use.
 *
 * @param {{
 *   value: string[],
 *   onChange: Function,
 *   civilCase?: boolean,
 *   showBoth?: boolean,
 * }} props
 */
export function DismissalReasonChips({ value, onChange, civilCase = false, showBoth = false }) {
  const strikeSide = civilCase ? 'Plaintiff' : 'Pros.';
  const options = [
    { key: 'cause', label: 'Cause' },
    { key: 'hardship', label: 'Hardship' },
    { key: 'prosecution', label: `${strikeSide} strike` },
    { key: 'defense', label: 'Def. strike' },
    ...(showBoth ? [{ key: 'both', label: 'Both strike' }] : []),
  ];

  return <FilterChips options={options} value={value} onChange={onChange} />;
}

/** Intent (juror status) chips: Keep / Question / Dismiss. */
export function StatusFilterChips({ value, onChange }) {
  return <FilterChips options={JUROR_STATUSES} value={value} onChange={onChange} />;
}

/**
 * Compact multi-select of demographic values, grouped by category.
 * Options are derived from the jurors actually present in the area being
 * filtered, so there are never dead options. Renders nothing when the
 * jurors carry no demographics.
 *
 * @param {{
 *   jurors: Array,
 *   value: Array<{name: string, value: string}>,
 *   onChange: Function,
 *   placeholder?: string,
 *   minWidth?: number,
 * }} props
 */
export function DemographicsFilterSelect({
  jurors,
  value,
  onChange,
  placeholder = 'Filter by demographic',
  minWidth = 240,
  maxWidth = 340,
}) {
  const options = React.useMemo(() => {
    const seen = new Set();
    const opts = [];
    for (const juror of jurors) {
      for (const d of juror.demographics || []) {
        if (!d?.name || !d?.value) continue;
        const key = `${d.name} ${d.value}`;
        if (seen.has(key)) continue;
        seen.add(key);
        opts.push({ name: d.name, value: d.value, key });
      }
    }
    // Selections are shared across areas — include ones this area's jurors
    // don't carry so MUI doesn't warn about values missing from options.
    for (const sel of value) {
      if (!seen.has(sel.key)) {
        seen.add(sel.key);
        opts.push(sel);
      }
    }
    opts.sort(
      (a, b) => a.name.localeCompare(b.name) || a.value.localeCompare(b.value),
    );
    return opts;
  }, [jurors, value]);

  if (options.length === 0 && value.length === 0) {
    return null;
  }

  return (
    <Autocomplete
      multiple
      size="small"
      disableCloseOnSelect
      options={options}
      groupBy={(o) => o.name}
      getOptionLabel={(o) => o.value}
      isOptionEqualToValue={(o, v) => o.key === v.key}
      value={value}
      onChange={(e, next) => onChange(next)}
      limitTags={2}
      style={{ minWidth, maxWidth }}
      sx={{
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
          minHeight: FILTER_CONTROL_HEIGHT,
          paddingTop: '1px',
          paddingBottom: '1px',
          fontSize: FILTER_FONT_SIZE,
          backgroundColor: FILTER_BG,
          borderRadius: `${FILTER_RADIUS}px`,
        },
      }}
      renderTags={(selected, getTagProps) =>
        selected.map((opt, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              key={opt.key}
              size="small"
              label={`${opt.name}: ${opt.value}`}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          placeholder={value.length === 0 ? placeholder : ''}
          inputProps={{
            ...params.inputProps,
            style: { ...params.inputProps?.style, fontSize: FILTER_FONT_SIZE },
          }}
        />
      )}
    />
  );
}
